import axios from 'axios';

const GENERIC_SERVER_MESSAGES = new Set([
  'server error',
  'internal server error',
  'network error',
  'something went wrong',
  'request failed',
]);

/** Maps API `error` codes from billing / wallet endpoints to curator-friendly copy. */
export const BILLING_API_ERROR_BY_CODE: Record<string, string> = {
  insufficient_balance:
    'Not enough available wallet balance. Eligible and pending earnings cannot be used — switch to card or wait until funds become available.',
  requires_card_first:
    'Your first subscription must be paid by card. After that, you can use wallet credits for renewals and upgrades.',
  prefers_card:
    'Set default payment to Credits under Account before paying for a plan from your wallet.',
  metadata_missing:
    'We could not find your billing profile. Complete a card checkout once, then try again.',
  missing_price: 'Please select a plan and try again.',
  invoice_missing:
    'The upgrade did not create an invoice we could pay from credits. Try again or pay by card.',
  payment_failed:
    'Could not pay from wallet credits. Check your available balance or use your card.',
  no_subscription_item:
    'Your subscription could not be updated. Contact support if this continues.',
  debit_failed:
    'Could not debit your wallet for this plan. Check your available balance or pay by card.',
};

type ApiErrorBody = {
  message?: string;
  error?: string;
};

const readErrorBody = (error: unknown): ApiErrorBody | null => {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;
  if (!data || typeof data !== 'object') return null;
  return data as ApiErrorBody;
};

const isGenericServerMessage = (message: string): boolean =>
  GENERIC_SERVER_MESSAGES.has(message.trim().toLowerCase());

/**
 * Prefer API `message` when it is specific; otherwise map `error` codes or use fallback.
 * Avoids showing raw "Server error" to curators.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
  codeMessages: Record<string, string> = BILLING_API_ERROR_BY_CODE
): string => {
  const body = readErrorBody(error);
  const code = typeof body?.error === 'string' ? body.error : undefined;
  const rawMessage = typeof body?.message === 'string' ? body.message.trim() : '';

  if (code && codeMessages[code]) {
    if (!rawMessage || isGenericServerMessage(rawMessage)) {
      return codeMessages[code];
    }
  }

  if (rawMessage && !isGenericServerMessage(rawMessage)) {
    return rawMessage;
  }

  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  if (error.code === 'ERR_NETWORK') {
    return 'Could not reach Songplace. Check your connection and try again.';
  }

  const status = error.response?.status;
  if (status && status >= 500) {
    return fallback;
  }

  return fallback;
};
